import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import {
  DatasetDetailDto,
  RecentVisitorMessagesResponseDto,
  SelectedRecentMessage,
} from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { RecentConversationsPicker } from '@/UI/Simulation/components/RecentConversationsPicker';
import {
  MAX_DATASET_MESSAGES,
  MAX_USER_MESSAGE_CHARS,
  parsePastedMessages,
} from '@/UI/Simulation/components/simulationUtils';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type TabValue = 'from_inbox' | 'paste' | 'csv';

export const CreateDatasetSheet = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation('simulation_page');
  const { capture } = usePostHogAnalytics();
  const [tab, setTab] = useState<TabValue>('paste');
  const [name, setName] = useState('');
  const [messages, setMessages] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [criteria, setCriteria] = useState('');
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [metadata, setMetadata] = useState('');
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataError, setMetadataError] = useState(false);
  const [selectedFromInbox, setSelectedFromInbox] = useState<SelectedRecentMessage[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: defaultCriteria } = useQuery<{ evaluation_criteria: string }>({
    queryKey: [QueryKey.simulation.defaultEvaluationCriteria],
    enabled: open,
  });

  const { data: recent, isLoading: recentLoading } = useQuery<RecentVisitorMessagesResponseDto>({
    queryKey: [QueryKey.simulation.recentVisitorMessages],
    enabled: open,
  });

  useEffect(() => {
    if (open && !criteria && defaultCriteria?.evaluation_criteria) {
      setCriteria(defaultCriteria.evaluation_criteria);
    }
  }, [open, criteria, defaultCriteria]);

  const templates = useMemo(
    () => [
      t('template_prompt_1'),
      t('template_prompt_2'),
      t('template_prompt_3'),
      t('template_prompt_4'),
      t('template_prompt_5'),
      t('template_prompt_6'),
    ],
    [t],
  );

  // Fall back to templates not just when the org has never had a real
  // conversation, but also when none of the recent ones surfaced a usable
  // visitor message (operator-only conversations, etc.) — otherwise the picker
  // would render an empty list with no actionable next step.
  const templateMode = !recent?.has_real_conversations || (recent?.items.length ?? 0) === 0;

  const createMutation = useMutation<
    DatasetDetailDto,
    unknown,
    {
      name: string;
      description?: string;
      evaluation_criteria?: string;
      conversation_metadata?: Record<string, unknown>;
    }
  >({
    mutationKey: [QueryKey.simulation.createDataset],
  });
  const addMessagesMutation = useMutation<
    DatasetDetailDto,
    unknown,
    { datasetId: number; messages: { user_message: string }[] }
  >({ mutationKey: [QueryKey.simulation.addMessages] });
  const updateMutation = useMutation<
    DatasetDetailDto,
    unknown,
    {
      datasetId: number;
      evaluation_criteria?: string;
      conversation_metadata?: Record<string, unknown>;
    }
  >({ mutationKey: [QueryKey.simulation.updateDataset] });
  const uploadCsvMutation = useMutation<DatasetDetailDto, unknown, { name: string; file: File }>({
    mutationKey: [QueryKey.simulation.uploadCsv],
  });

  const reset = () => {
    setName('');
    setMessages('');
    setFile(null);
    setCriteria(defaultCriteria?.evaluation_criteria ?? '');
    setCriteriaOpen(false);
    setMetadata('');
    setMetadataOpen(false);
    setMetadataError(false);
    setSelectedFromInbox([]);
    setTab('paste');
  };

  // Returns the parsed metadata dict, or undefined if the editor is empty.
  // Returns null when the input is non-empty but invalid (caller must guard).
  const parseMetadata = (): Record<string, unknown> | undefined | null => {
    if (!metadata.trim()) return undefined;
    try {
      const parsed = JSON.parse(metadata);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const handleMetadataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMetadata(value);
    if (!value.trim()) {
      setMetadataError(false);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      setMetadataError(typeof parsed !== 'object' || parsed === null || Array.isArray(parsed));
    } catch {
      setMetadataError(true);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const parsedMetadata = parseMetadata();
    if (parsedMetadata === null) return; // invalid JSON — submit button should already be disabled

    const hasCustomCriteria =
      !!criteria && criteria !== (defaultCriteria?.evaluation_criteria ?? '');
    const hasMetadata = parsedMetadata !== undefined;

    if (tab === 'from_inbox' && selectedFromInbox.length > 0) {
      const chosen = selectedFromInbox.slice(0, MAX_DATASET_MESSAGES);
      createMutation.mutate(
        {
          name: name.trim(),
          evaluation_criteria: criteria || undefined,
          conversation_metadata: parsedMetadata,
        },
        {
          onSuccess: (dataset) => {
            addMessagesMutation.mutate(
              {
                datasetId: dataset.id,
                messages: chosen.map((m) => ({ user_message: m.user_message })),
              },
              {
                onSuccess: () => {
                  capture(PostHogEventEnum.SimulationDatasetCreated, {
                    source: 'from_inbox',
                    message_count: chosen.length,
                    has_custom_criteria: hasCustomCriteria,
                    has_metadata: hasMetadata,
                  });
                  toast.success(t('toast_dataset_created_from_inbox', { count: chosen.length }));
                  reset();
                  onOpenChange(false);
                },
                onError: () => toast.error(t('toast_messages_error')),
              },
            );
          },
          onError: () => toast.error(t('toast_create_error')),
        },
      );
      return;
    }

    if (tab === 'csv' && file) {
      uploadCsvMutation.mutate(
        { name: name.trim(), file },
        {
          onSuccess: (ds) => {
            // CSV upload doesn't accept criteria/metadata — patch after create if customized.
            const needsCriteriaPatch = hasCustomCriteria;
            const needsMetadataPatch = hasMetadata;
            if (needsCriteriaPatch || needsMetadataPatch) {
              updateMutation.mutate({
                datasetId: ds.id,
                ...(needsCriteriaPatch ? { evaluation_criteria: criteria } : {}),
                ...(needsMetadataPatch ? { conversation_metadata: parsedMetadata } : {}),
              });
            }
            capture(PostHogEventEnum.SimulationDatasetCsvUploaded, {
              file_size_bytes: file.size,
              message_count: ds.message_count,
            });
            capture(PostHogEventEnum.SimulationDatasetCreated, {
              source: 'csv',
              message_count: ds.message_count,
              has_custom_criteria: hasCustomCriteria,
              has_metadata: hasMetadata,
            });
            toast.success(t('toast_dataset_created_csv'));
            reset();
            onOpenChange(false);
          },
          onError: () => toast.error(t('toast_csv_error')),
        },
      );
    } else if (tab === 'paste' && messages.trim()) {
      const parsed = parsePastedMessages(messages);
      if (parsed.length === 0) return;
      createMutation.mutate(
        {
          name: name.trim(),
          evaluation_criteria: criteria || undefined,
          conversation_metadata: parsedMetadata,
        },
        {
          onSuccess: (dataset) => {
            addMessagesMutation.mutate(
              {
                datasetId: dataset.id,
                messages: parsed.map((m) => ({ user_message: m })),
              },
              {
                onSuccess: () => {
                  capture(PostHogEventEnum.SimulationDatasetCreated, {
                    source: 'paste',
                    message_count: parsed.length,
                    has_custom_criteria: hasCustomCriteria,
                    has_metadata: hasMetadata,
                  });
                  toast.success(t('toast_dataset_created', { count: parsed.length }));
                  reset();
                  onOpenChange(false);
                },
                onError: () => toast.error(t('toast_messages_error')),
              },
            );
          },
          onError: () => toast.error(t('toast_create_error')),
        },
      );
    }
  };

  const parsedPasted = parsePastedMessages(messages);
  const pastedCount = parsedPasted.length;
  const exceedsMax = pastedCount > MAX_DATASET_MESSAGES;
  const tooLongCount = parsedPasted.filter((m) => m.length > MAX_USER_MESSAGE_CHARS).length;
  const exceedsCharLimit = tooLongCount > 0;

  const isSubmitting =
    createMutation.isPending || addMessagesMutation.isPending || uploadCsvMutation.isPending;
  const inboxSelectionExceedsMax = selectedFromInbox.length > MAX_DATASET_MESSAGES;
  const noMessagesYet =
    tab === 'paste' ? pastedCount === 0 : tab === 'csv' ? !file : selectedFromInbox.length === 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!max-w-2xl w-full overflow-hidden">
        <SheetHeader>
          <SheetTitle>{t('create_dataset')}</SheetTitle>
          <section className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !name.trim() ||
                isSubmitting ||
                metadataError ||
                noMessagesYet ||
                (tab === 'paste' && (exceedsMax || exceedsCharLimit)) ||
                (tab === 'from_inbox' && inboxSelectionExceedsMax)
              }
            >
              {isSubmitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('create')}
            </Button>
          </section>
        </SheetHeader>

        <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1">
          <Input
            placeholder={t('dataset_name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {name.trim() && noMessagesYet && (
            <p data-testid="create-needs-messages-hint" className="text-xs text-muted-foreground">
              {t('create_needs_messages')}
            </p>
          )}

          <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
            <TabsList className="w-full">
              <TabsTrigger value="paste" className="flex-1" data-testid="tab-paste">
                {t('paste_messages')}
              </TabsTrigger>
              <TabsTrigger value="from_inbox" className="flex-1" data-testid="tab-from-inbox">
                {t('tab_from_inbox')}
              </TabsTrigger>
              <TabsTrigger value="csv" className="flex-1">
                {t('upload_csv')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="from_inbox" className="mt-3">
              <RecentConversationsPicker
                items={recent?.items ?? []}
                templates={templates}
                templateMode={templateMode}
                selected={selectedFromInbox}
                onChange={setSelectedFromInbox}
                loading={recentLoading}
              />
              {inboxSelectionExceedsMax && (
                <p className="mt-1.5 text-xs text-destructive">
                  {t('paste_messages_max_exceeded', { max: MAX_DATASET_MESSAGES })}
                </p>
              )}
            </TabsContent>
            <TabsContent value="paste" className="mt-3">
              <textarea
                data-testid="paste-messages-textarea"
                className={`h-40 w-full resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${
                  exceedsMax || exceedsCharLimit
                    ? 'border-destructive text-destructive focus:ring-destructive/30'
                    : ''
                }`}
                placeholder={t('paste_placeholder')}
                value={messages}
                onChange={(e) => setMessages(e.target.value)}
              />
              <div
                className={`mt-1.5 flex items-start justify-between gap-3 text-xs ${
                  exceedsMax || exceedsCharLimit ? 'text-destructive' : 'text-muted-foreground'
                }`}
              >
                <span>
                  {exceedsMax
                    ? t('paste_messages_max_exceeded', { max: MAX_DATASET_MESSAGES })
                    : exceedsCharLimit
                      ? t('paste_messages_too_long', {
                          count: tooLongCount,
                          max: MAX_USER_MESSAGE_CHARS,
                        })
                      : t('paste_separator_hint')}
                </span>
                {pastedCount > 0 && (
                  <span data-testid="paste-messages-count" className="shrink-0 tabular-nums">
                    {t('messages_count', { count: pastedCount })}
                  </span>
                )}
              </div>
            </TabsContent>
            <TabsContent value="csv" className="mt-3 space-y-1.5">
              <div
                className="flex h-40 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed transition-colors hover:bg-accent/30"
                onClick={() => fileRef.current?.click()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <div className="flex items-center gap-2">
                    <Upload className="h-4 w-4 text-success" />
                    <span className="text-sm">{file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">{t('csv_upload_prompt')}</p>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('csv_upload_hint')}</p>
              <p className="text-xs text-muted-foreground">
                {t('csv_truncation_hint', { max: MAX_DATASET_MESSAGES })}
              </p>
            </TabsContent>
          </Tabs>

          {/* Evaluation criteria (collapsible) */}
          <div className="rounded-md border">
            <button
              type="button"
              data-testid="eval-criteria-toggle"
              onClick={() => setCriteriaOpen((v) => !v)}
              className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium"
            >
              <span className="flex items-center gap-1.5">
                {criteriaOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                {t('evaluation_criteria_label')}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {t('evaluation_criteria_hint')}
              </span>
            </button>
            {criteriaOpen && (
              <div className="space-y-2 border-t px-3 pb-3 pt-2">
                <textarea
                  data-testid="eval-criteria-input"
                  className="h-48 w-full resize-none rounded-md border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
                  value={criteria}
                  onChange={(e) => setCriteria(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('evaluation_criteria_tip')}</p>
              </div>
            )}
          </div>

          {/* Conversation metadata (collapsible) */}
          <div className="rounded-md border">
            <button
              type="button"
              data-testid="conversation-metadata-toggle"
              onClick={() => setMetadataOpen((v) => !v)}
              className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium"
            >
              <span className="flex items-center gap-1.5">
                {metadataOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                {t('conversation_metadata_label')}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {t('conversation_metadata_hint')}
              </span>
            </button>
            {metadataOpen && (
              <div className="space-y-2 border-t px-3 pb-3 pt-2">
                <textarea
                  data-testid="conversation-metadata-input"
                  className={`h-48 w-full resize-none rounded-md border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring ${
                    metadataError ? 'border-destructive focus:ring-destructive/30' : ''
                  }`}
                  placeholder='{"locale": "pl", "user_type": "vip"}'
                  value={metadata}
                  onChange={handleMetadataChange}
                />
                {metadataError && (
                  <p
                    className="text-xs text-destructive"
                    data-testid="conversation-metadata-create-error"
                  >
                    {t('conversation_metadata_invalid_json')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
